import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Check,
  Copy,
  Eye,
  EyeOff,
  Pencil,
  RefreshCw,
  Radio,
  X,
} from "lucide-react";

import Button from "../../../../../components/buttons/Button";
import CreatorEditStreamForm from "../forms/CreatorEditStreamForm";

import { useCreatorStreams } from "../../../creators/hooks/useCreatorStreams";

import "./CreatorStreamSetupForm.css";

const getErrorMessage = (error, fallback = "Something went wrong.") => {
  if (!error) return "";

  if (typeof error === "string") return error;

  return error?.response?.data?.message || error?.message || fallback;
};

const formatLabel = (value) =>
  value ? String(value).replaceAll("_", " ") : "N/A";

const formatDateTime = (value) => {
  if (!value) return "Not set";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Invalid date";

  return date.toLocaleString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getStreamStatus = (status) =>
  String(status || "scheduled")
    .trim()
    .toLowerCase();

export default function CreatorStreamSetupForm({
  streamSessionId,
  onClose,
  closeModal,
  onDeleted,
}) {
  const {
    broadcastData,
    loading,
    error,
    getStreamBroadcastData,
    markStreamLive,
    markStreamEnded,
    deleteStream,
    updateStreamDetails,
    linkIvsRecordingToStream,
    resetStreamState,
  } = useCreatorStreams();

  const [activeTab, setActiveTab] = useState("broadcast");
  const [activeAction, setActiveAction] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [copiedField, setCopiedField] = useState("");
  const [showStreamKey, setShowStreamKey] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [initializing, setInitializing] = useState(true);

  const copyTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const actionRef = useRef(false);

  const closeView = onClose || closeModal;
  const stream = broadcastData?.streamSessionId ? broadcastData : null;
  const status = getStreamStatus(stream?.status);

  const isLive = status === "live";
  const isEnded = status === "ended";
  const isBusy = loading || Boolean(activeAction);

  const recordingEnabled =
    stream?.recordingEnabled === true ||
    stream?.recording_enabled === true ||
    Number(stream?.recordingEnabled ?? stream?.recording_enabled) === 1;

  const replayUrl =
    stream?.vodHlsUrl ||
    stream?.vod_hls_url ||
    stream?.vodMp4Url ||
    stream?.vod_mp4_url ||
    "";

  const hasReplay = Boolean(replayUrl);

  const serverUrl = String(stream?.obsServerUrl || "");
  const streamKey = String(stream?.ivsStreamKeyValue || "");

  const displayError = actionError || getErrorMessage(error, "");

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      if (copyTimerRef.current) {
        window.clearTimeout(copyTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadBroadcast = async () => {
      setInitializing(true);
      setActionError("");
      setActionMessage("");
      setActiveTab("broadcast");
      setEditingDetails(false);
      setShowStreamKey(false);

      if (!streamSessionId) {
        setInitializing(false);
        return;
      }

      try {
        await getStreamBroadcastData(streamSessionId);
      } catch (loadError) {
        if (active) {
          setActionError(
            getErrorMessage(loadError, "Unable to load broadcast setup."),
          );
        }
      } finally {
        if (active) setInitializing(false);
      }
    };

    void loadBroadcast();

    return () => {
      active = false;
      resetStreamState();
    };
  }, [streamSessionId]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (
        event.key === "Escape" &&
        !isBusy &&
        !editingDetails &&
        !actionRef.current
      ) {
        event.preventDefault();
        closeView?.();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isBusy, editingDetails, closeView]);

  const handleClose = () => {
    if (isBusy || editingDetails || actionRef.current) return;
    closeView?.();
  };

  const refreshBroadcastData = async () => {
    if (!streamSessionId) return false;

    return getStreamBroadcastData(streamSessionId);
  };

  const handleRefresh = async () => {
    if (isBusy || actionRef.current) return;

    actionRef.current = true;
    setActiveAction("refresh");
    setActionError("");
    setActionMessage("");

    try {
      const result = await refreshBroadcastData();

      if (result === false || result?.success === false) {
        throw new Error(result?.message || "Unable to refresh stream.");
      }

      if (mountedRef.current) {
        setActionMessage("Broadcast information updated.");
      }
    } catch (refreshError) {
      if (mountedRef.current) {
        setActionError(
          getErrorMessage(refreshError, "Unable to refresh stream."),
        );
      }
    } finally {
      actionRef.current = false;

      if (mountedRef.current) setActiveAction("");
    }
  };

  const handleSaveStreamDetails = async (payload) => {
    if (!streamSessionId || !payload) return false;

    const result = await updateStreamDetails(streamSessionId, payload);

    if (result === false || result?.success === false) {
      throw new Error(result?.message || "Unable to update stream details.");
    }

    await refreshBroadcastData();

    setEditingDetails(false);
    setActionMessage("Stream details updated.");

    return result;
  };

  const copyToClipboard = async (value, fieldName) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(String(value));

      setCopiedField(fieldName);
      setActionError("");

      if (copyTimerRef.current) {
        window.clearTimeout(copyTimerRef.current);
      }

      copyTimerRef.current = window.setTimeout(() => {
        if (mountedRef.current) setCopiedField("");
      }, 1500);
    } catch (copyError) {
      setActionError(
        getErrorMessage(copyError, "Unable to copy. Please copy manually."),
      );
    }
  };

  const runAction = async (name, callback, successMessage) => {
    if (!streamSessionId || isBusy || actionRef.current) return;

    actionRef.current = true;
    setActiveAction(name);
    setActionError("");
    setActionMessage("");

    try {
      const result = await callback();

      if (result === false || result?.success === false) {
        throw new Error(result?.message || `Unable to ${name} stream.`);
      }

      await refreshBroadcastData();

      if (mountedRef.current) {
        const message =
          typeof successMessage === "function"
            ? successMessage(result)
            : successMessage;

        setActionMessage(message || "");
      }

      return result;
    } catch (operationError) {
      console.error(`Stream action failed (${name}):`, operationError);

      if (mountedRef.current) {
        setActionError(
          getErrorMessage(operationError, `Unable to ${name} stream.`),
        );
      }

      return false;
    } finally {
      actionRef.current = false;

      if (mountedRef.current) setActiveAction("");
    }
  };

  const handleStartStream = () =>
    runAction(
      "start",
      () => markStreamLive(streamSessionId),
      "Stream marked as live.",
    );

  const handleEndStream = async () => {
    if (!window.confirm("Are you sure you want to end this stream?")) {
      return;
    }

    await runAction(
      "end",
      () => markStreamEnded(streamSessionId),
      (result) => {
        const replayReady = Boolean(
          result?.replayReady || result?.vodHlsUrl || result?.vodMp4Url,
        );

        return replayReady
          ? "Stream ended. Replay is ready."
          : "Stream ended. Replay may take time to become available.";
      },
    );
  };

  const handleLinkIvsReplay = () =>
    runAction(
      "link",
      () => linkIvsRecordingToStream(streamSessionId),
      (result) => {
        const replayReady = Boolean(
          result?.stream?.vodHlsUrl ||
          result?.stream?.vodMp4Url ||
          result?.recording?.playbackUrl,
        );

        return replayReady
          ? "IVS replay linked successfully."
          : "IVS recording is not ready yet. Try again later.";
      },
    );

  const handleDeleteStream = async () => {
    if (isBusy || actionRef.current) return;

    const confirmed = window.confirm(
      `Delete "${stream?.title || "this stream"}"?\n\nThis will permanently delete the stream and its associated files.`,
    );

    if (!confirmed) return;

    const finalConfirmation = window.confirm(
      "Final confirmation: permanently delete this stream?",
    );

    if (!finalConfirmation) return;

    actionRef.current = true;
    setActiveAction("delete");
    setActionError("");
    setActionMessage("");

    try {
      const result = await deleteStream(streamSessionId);

      if (result === false || result?.success === false) {
        throw new Error(result?.message || "Unable to delete stream.");
      }

      await onDeleted?.(result);
      closeView?.();
    } catch (deleteError) {
      if (mountedRef.current) {
        setActionError(
          getErrorMessage(deleteError, "Unable to delete stream."),
        );
      }
    } finally {
      actionRef.current = false;

      if (mountedRef.current) setActiveAction("");
    }
  };

  const details = [
    {
      label: "Stream Type",
      value: formatLabel(stream?.streamType || stream?.stream_type),
    },
    {
      label: "Access",
      value: formatLabel(stream?.accessType || stream?.access_type),
    },
    {
      label: "Scheduled",
      value: formatDateTime(stream?.scheduledStart || stream?.scheduled_start),
    },
    {
      label: "Recording",
      value: recordingEnabled ? "Enabled" : "Off",
    },
    {
      label: "Started",
      value: formatDateTime(stream?.actualStart || stream?.actual_start),
    },
    {
      label: "Ended",
      value: formatDateTime(stream?.actualEnd || stream?.actual_end),
    },
    {
      label: "Replay Status",
      value: formatLabel(
        stream?.mediaConvertStatus || stream?.media_convert_status,
      ),
    },
    {
      label: "Channel",
      value: stream?.ivsChannelName || stream?.ivs_channel_name || "N/A",
    },
  ];

  return (
    <>
      {createPortal(
        <div className="creator-stream-setup" aria-label="Broadcast Setup">
          <header className="creator-stream-setup__header">
            <div className="creator-stream-setup__header-info">
              <h2>Broadcast Setup</h2>
              <p>Manage your live stream and broadcast settings.</p>
            </div>

            <button
              type="button"
              className="creator-stream-setup__icon-button"
              onClick={handleClose}
              disabled={isBusy || editingDetails}
              aria-label="Close broadcast setup"
            >
              <X size={20} />
            </button>
          </header>

          <div className="creator-stream-setup__workspace">
            <main className="creator-stream-setup__main">
              {!stream ? (
                <div className="creator-stream-setup__empty" role="status">
                  {initializing || loading
                    ? "Loading broadcast setup..."
                    : displayError || "No broadcast data found."}
                </div>
              ) : (
                <>
                  <div className="creator-stream-setup__stream-info">
                    <div className="creator-stream-setup__stream-title">
                      <Radio size={22} />

                      <div>
                        <h3>{stream.title}</h3>

                        {stream.description && <p>{stream.description}</p>}
                      </div>
                    </div>

                    <div className="creator-stream-setup__stream-meta">
                      <span>Stream ID</span>
                      <strong>{stream.streamSessionId}</strong>
                    </div>
                  </div>

                  <div
                    className="creator-stream-setup__tabs"
                    role="tablist"
                    aria-label="Stream setup sections"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeTab === "broadcast"}
                      className={activeTab === "broadcast" ? "active" : ""}
                      onClick={() => setActiveTab("broadcast")}
                    >
                      Broadcast Setup
                    </button>

                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeTab === "details"}
                      className={activeTab === "details" ? "active" : ""}
                      onClick={() => setActiveTab("details")}
                    >
                      Stream Details
                    </button>
                  </div>

                  {activeTab === "broadcast" ? (
                    <section
                      className="creator-stream-setup__panel"
                      role="tabpanel"
                    >
                      <div className="creator-stream-setup__section-header">
                        <h3>OBS / YoloBox Settings</h3>
                        <p>
                          Use these credentials to connect your streaming
                          software or encoder.
                        </p>
                      </div>

                      <div className="creator-stream-setup__card">
                        <div className="creator-stream-setup__field">
                          <label htmlFor="stream-setup-server">
                            Server URL
                          </label>

                          <div className="creator-stream-setup__input-row">
                            <input
                              id="stream-setup-server"
                              type="text"
                              value={serverUrl}
                              readOnly
                            />

                            <button
                              type="button"
                              className="creator-stream-setup__icon-button"
                              onClick={() =>
                                copyToClipboard(serverUrl, "server")
                              }
                              disabled={!serverUrl}
                              aria-label="Copy server URL"
                              title={
                                copiedField === "server"
                                  ? "Copied"
                                  : "Copy server URL"
                              }
                            >
                              {copiedField === "server" ? (
                                <Check size={17} />
                              ) : (
                                <Copy size={17} />
                              )}
                            </button>
                          </div>
                        </div>

                        <div className="creator-stream-setup__field">
                          <label htmlFor="stream-setup-key">Stream Key</label>

                          <div className="creator-stream-setup__input-row">
                            <input
                              id="stream-setup-key"
                              type={showStreamKey ? "text" : "password"}
                              value={streamKey}
                              readOnly
                              autoComplete="off"
                              spellCheck={false}
                            />

                            <button
                              type="button"
                              className="creator-stream-setup__icon-button"
                              onClick={() =>
                                setShowStreamKey((current) => !current)
                              }
                              disabled={!streamKey}
                              aria-label={
                                showStreamKey
                                  ? "Hide stream key"
                                  : "Show stream key"
                              }
                              title={
                                showStreamKey
                                  ? "Hide stream key"
                                  : "Show stream key"
                              }
                            >
                              {showStreamKey ? (
                                <EyeOff size={17} />
                              ) : (
                                <Eye size={17} />
                              )}
                            </button>

                            <button
                              type="button"
                              className="creator-stream-setup__icon-button"
                              onClick={() => copyToClipboard(streamKey, "key")}
                              disabled={!streamKey}
                              aria-label="Copy stream key"
                              title={
                                copiedField === "key"
                                  ? "Copied"
                                  : "Copy stream key"
                              }
                            >
                              {copiedField === "key" ? (
                                <Check size={17} />
                              ) : (
                                <Copy size={17} />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="creator-stream-setup__section-header">
                        <h3>Connection Instructions</h3>
                        <p>Configure your encoder to start broadcasting.</p>
                      </div>

                      <div className="creator-stream-setup__instructions">
                        <ol>
                          <li>
                            Open OBS and navigate to{" "}
                            <strong>Settings → Stream</strong>.
                          </li>
                          <li>
                            Select <strong>Custom</strong> as the service.
                          </li>
                          <li>
                            Paste the <strong>Server URL</strong> and{" "}
                            <strong>Stream Key</strong>.
                          </li>
                          <li>
                            Click <strong>Start Streaming</strong> in OBS or
                            your YoloBox.
                          </li>
                        </ol>
                      </div>
                    </section>
                  ) : (
                    <section
                      className="creator-stream-setup__panel"
                      role="tabpanel"
                    >
                      <div className="creator-stream-setup__section-header creator-stream-setup__section-header--action">
                        <div>
                          <h3>Stream Details</h3>
                          <p>Review and update your stream information.</p>
                        </div>

                        <Button
                          value="Edit Details"
                          variant="primary-outlined"
                          onClick={() => setEditingDetails(true)}
                          disabled={isBusy}
                          icon={<Pencil size={16} />}
                          style={{ width: "fit-content" }}
                        />
                      </div>

                      <div className="creator-stream-setup__details">
                        {details.map((item) => (
                          <div
                            className="creator-stream-setup__detail"
                            key={item.label}
                          >
                            <span>{item.label}</span>
                            <strong>{item.value}</strong>
                          </div>
                        ))}
                      </div>

                      <div className="creator-stream-setup__section-header">
                        <h3>Replay Details</h3>
                        <p>
                          Replay information becomes available after the
                          recording is linked.
                        </p>
                      </div>

                      <div className="creator-stream-setup__card">
                        <div className="creator-stream-setup__replay-row">
                          <div>
                            <span>Replay</span>
                            <strong>
                              {hasReplay ? "Available" : "Not ready"}
                            </strong>
                          </div>

                          {hasReplay && (
                            <button
                              type="button"
                              className="creator-stream-setup__icon-button"
                              onClick={() =>
                                copyToClipboard(replayUrl, "replay")
                              }
                              aria-label="Copy replay URL"
                              title={
                                copiedField === "replay"
                                  ? "Copied"
                                  : "Copy replay URL"
                              }
                            >
                              {copiedField === "replay" ? (
                                <Check size={17} />
                              ) : (
                                <Copy size={17} />
                              )}
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="creator-stream-setup__danger">
                        <div>
                          <h3>Delete Stream</h3>
                          <p>
                            Permanently delete this stream and its associated
                            records and files.
                          </p>
                        </div>

                        <Button
                          value={
                            activeAction === "delete"
                              ? "Deleting..."
                              : "Delete Stream"
                          }
                          variant="primary-outlined"
                          onClick={handleDeleteStream}
                          disabled={isBusy}
                          style={{ width: "fit-content" }}
                        />
                      </div>
                    </section>
                  )}
                </>
              )}
            </main>

            <aside className="creator-stream-setup__sidebar">
              <div className="creator-stream-setup__section-header">
                <h3>Broadcast Status</h3>
                <p>Monitor and manage your stream.</p>
              </div>

              <div className="creator-stream-setup__status-card">
                <span>Current Status</span>

                <div className="creator-stream-setup__status-line">
                  <span
                    className={`creator-stream-setup__status-dot creator-stream-setup__status-dot--${status}`}
                  />

                  <strong>{formatLabel(status)}</strong>
                </div>
              </div>

              <div className="creator-stream-setup__summary">
                <h4>Stream Summary</h4>

                <div>
                  <span>Recording</span>
                  <strong>{recordingEnabled ? "Enabled" : "Disabled"}</strong>
                </div>

                <div>
                  <span>Replay</span>
                  <strong>{hasReplay ? "Available" : "Not ready"}</strong>
                </div>

                <div>
                  <span>Access</span>
                  <strong>
                    {formatLabel(stream?.accessType || stream?.access_type)}
                  </strong>
                </div>

                <div>
                  <span>Scheduled</span>
                  <strong>
                    {formatDateTime(
                      stream?.scheduledStart || stream?.scheduled_start,
                    )}
                  </strong>
                </div>
              </div>

              <div className="creator-stream-setup__controls">
                <div className="creator-stream-setup__section-header">
                  <h3>Broadcast Controls</h3>
                  <p>Manage the stream lifecycle.</p>
                </div>

                <Button
                  value={activeAction === "start" ? "Starting..." : "Mark Live"}
                  variant="primary"
                  onClick={handleStartStream}
                  disabled={isBusy || !stream || isLive || isEnded}
                />

                <Button
                  value={activeAction === "end" ? "Ending..." : "End Stream"}
                  variant="primary-outlined"
                  onClick={handleEndStream}
                  disabled={isBusy || !isLive || isEnded}
                />

                {isEnded && !hasReplay && recordingEnabled && (
                  <Button
                    value={
                      activeAction === "link" ? "Linking..." : "Link IVS Replay"
                    }
                    variant="primary-outlined"
                    onClick={handleLinkIvsReplay}
                    disabled={isBusy}
                  />
                )}

                <p className="creator-stream-setup__help">
                  {!isEnded
                    ? "Stop streaming from OBS or YoloBox before ending the stream."
                    : hasReplay
                      ? "Your IVS replay is linked and available."
                      : "Once IVS finishes preparing the recording, you can link the replay."}
                </p>
              </div>
            </aside>
          </div>

          <footer className="creator-stream-setup__footer">
            <div className="creator-stream-setup__footer-status">
              {displayError ? (
                <span className="creator-stream-setup__error" role="alert">
                  {displayError}
                </span>
              ) : actionMessage ? (
                <span className="creator-stream-setup__success" role="status">
                  {actionMessage}
                </span>
              ) : isBusy ? (
                <span>Updating broadcast information...</span>
              ) : null}
            </div>

            <div className="creator-stream-setup__actions">
              <button
                type="button"
                className="creator-stream-setup__icon-button"
                onClick={handleRefresh}
                disabled={isBusy || !stream}
                aria-label="Refresh broadcast information"
                title="Refresh"
              >
                <RefreshCw size={18} />
              </button>

              <Button
                value="Close"
                variant="primary-outlined"
                onClick={handleClose}
                disabled={isBusy || editingDetails}
              />
            </div>
          </footer>
        </div>,
        document.body,
      )}

      {editingDetails && stream && (
        <CreatorEditStreamForm
          stream={stream}
          loading={loading}
          error={error}
          onSave={handleSaveStreamDetails}
          onClose={() => setEditingDetails(false)}
        />
      )}
    </>
  );
}
